import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Navigation from './Navigation';
import apiService from '../services/api';
import { formatPrice } from '../constants/carFilters';
import Spinner from './Spinner';

/**
 * "Gebrauchtwagen" — privatni oglasi z gebrauchtwagen.at (Avstrija). POENOTEN filter (kot
 * Avtomobili): shranjen na strežniku (GebrauchtwagenFilter) je hkrati privzeti prikaz na
 * zaslonu (na vseh napravah) IN pogoj za samodejni nemški SMS. Ročni nemški SMS: gumb na
 * kartici. Dedup + blok-lista (po +43 številki) veljata enako kot na /sms; zapis nese externalId.
 * Vir: GET /cars/gebrauchtwagen (viden le adminu + userjem z gebrauchtwagenEnabled).
 */
const EMPTY = { znamka: '', model: '', cenaMin: '', cenaMax: '', letnikMin: '', letnikMax: '', kmMin: '', kmMax: '', gorivo: '', karoserija: '', lokacija: '' };
const num = (s) => { const n = parseInt(String(s == null ? '' : s).replace(/[^0-9]/g, ''), 10); return isNaN(n) ? null : n; };
const DEFAULT_DE = 'Guten Tag, ich interessiere mich für Ihr Fahrzeug. Ist es noch verfügbar? Bitte um Rückruf. Danke!';
const MAX_MSG = 100;

// Kanonična številka (mora se ujemati s SmsRules.normalizePhone na strežniku) — za "že poslano".
const normPhone = (phone) => {
  if (!phone) return '';
  let d = String(phone).replace(/[^0-9+]/g, '');
  if (d.startsWith('+')) d = d.slice(1);
  if (d.startsWith('00')) d = d.slice(2);
  d = d.replace(/[^0-9]/g, '');
  if (!d) return '';
  if (d.startsWith('386')) return d;
  if (d.startsWith('0')) return '386' + d.slice(1);
  return d;
};

export default function GebrauchtwagenBoard() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState(EMPTY);

  // SMS profil + predloga (nemščina) + "že poslano" mapa.
  const [tpl, setTpl] = useState('');
  const [tplSaved, setTplSaved] = useState('');
  const [atPhone, setAtPhone] = useState('');
  const [atPhoneSaved, setAtPhoneSaved] = useState('');
  const [savingTpl, setSavingTpl] = useState(false);
  const [profile, setProfile] = useState(null);
  const [sentMap, setSentMap] = useState({}); // normPhone -> ISO datum prvega pošiljanja

  // Faza 2b — POENOTEN filter: en shranjen filter je hkrati privzeti prikaz NA zaslonu IN
  // kriterij za samodejni nemški SMS (kot Avtomobili). autoEnabled = master stikalo.
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [savingFilter, setSavingFilter] = useState(false);
  const [fSnap, setFSnap] = useState({ f: EMPTY, enabled: false }); // shranjeno stanje (za "spremenjeno?")

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getGebrauchtwagenCars(168);
      setCars((res && res.data) || []);
    } catch (e) {
      /* non-critical */
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProfile = useCallback(async () => {
    try {
      const [p, sent, gf] = await Promise.all([
        apiService.getSmsProfile(),
        apiService.getSmsSentRecipients(),
        apiService.getGebrauchtwagenFilter().catch(() => null),
      ]);
      const d = (p && p.data) || {};
      const msg = d.gebrauchtwagenSmsMessage || '';
      const at = d.gebrauchtwagenContactPhone || '';
      setProfile(d);
      setTpl(msg);
      setTplSaved(msg);
      setAtPhone(at);
      setAtPhoneSaved(at);
      const map = {};
      ((sent && sent.data) || []).forEach((r) => { if (r.phone) map[r.phone] = r.firstSentAt; });
      setSentMap(map);
      // Poenoten shranjen filter -> seed prikaznega filtra (naročnik vedno vidi svoj pogled).
      const enabled = !!d.gebrauchtwagenAutoSms;
      const fd = (gf && gf.data) || {};
      const ff = {
        znamka: fd.brand || '', model: fd.model || '',
        cenaMin: fd.minPrice ?? '', cenaMax: fd.maxPrice ?? '',
        letnikMin: fd.minYear ?? '', letnikMax: fd.maxYear ?? '',
        kmMin: fd.minMileage ?? '', kmMax: fd.maxMileage ?? '',
        gorivo: fd.fuelType || '', karoserija: fd.bodyType || '', lokacija: fd.location || '',
      };
      setAutoEnabled(enabled);
      setF(ff);
      setFSnap({ enabled, f: ff });
    } catch (e) { /* non-critical */ }
  }, []);

  useEffect(() => { load(); loadProfile(); }, [load, loadProfile]);

  const saveTpl = async () => {
    setSavingTpl(true);
    try {
      const next = { ...(profile || {}), gebrauchtwagenSmsMessage: tpl, gebrauchtwagenContactPhone: atPhone };
      await apiService.updateSmsProfile(next);
      setProfile(next);
      setTplSaved(tpl);
      setAtPhoneSaved(atPhone);
    } catch (e) { /* prikaz napake preskočimo, ostane need, gumb ostane */ }
    finally { setSavingTpl(false); }
  };

  const toInt = (v) => { const n = parseInt(String(v).replace(/[^0-9]/g, ''), 10); return isNaN(n) ? null : n; };
  const filterDirty = autoEnabled !== fSnap.enabled || JSON.stringify(f) !== JSON.stringify(fSnap.f);

  // Shrani poenoten filter: velja za prikaz (ob naslednjem odprtju) IN za samodejni SMS.
  const saveFilter = async () => {
    setSavingFilter(true);
    try {
      const payload = {
        brand: f.znamka || null, model: f.model || null,
        minPrice: toInt(f.cenaMin), maxPrice: toInt(f.cenaMax),
        minYear: toInt(f.letnikMin), maxYear: toInt(f.letnikMax),
        minMileage: toInt(f.kmMin), maxMileage: toInt(f.kmMax),
        fuelType: f.gorivo || null, bodyType: f.karoserija || null, location: f.lokacija || null,
      };
      await apiService.saveGebrauchtwagenFilter(payload);
      await apiService.updateSmsProfile({ ...(profile || {}), gebrauchtwagenAutoSms: autoEnabled });
      setProfile((p) => ({ ...(p || {}), gebrauchtwagenAutoSms: autoEnabled }));
      setFSnap({ enabled: autoEnabled, f: { ...f } });
    } catch (e) { /* prikaz napake preskočimo */ }
    finally { setSavingFilter(false); }
  };

  // Opcije se izpeljejo iz naloženih oglasov (vedno točne za trenutno ponudbo).
  const distinct = (key) => [...new Set(cars.map((c) => c[key]).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));
  const znamke = useMemo(() => distinct('znamka'), [cars]);
  const goriva = useMemo(() => distinct('gorivo'), [cars]);
  const karoserije = useMemo(() => distinct('karoserija'), [cars]);

  const filtered = useMemo(() => cars.filter((c) => {
    if (f.znamka && c.znamka !== f.znamka) return false;
    if (f.model && !String(c.model || '').toLowerCase().includes(f.model.toLowerCase())) return false;
    if (f.gorivo && c.gorivo !== f.gorivo) return false;
    if (f.karoserija && c.karoserija !== f.karoserija) return false;
    if (f.lokacija && !String(c.viewLocation || '').toLowerCase().includes(f.lokacija.toLowerCase())) return false;
    const cena = num(c.cena), letnik = num(c.letnik), km = num(c.mileage);
    if (f.cenaMin && (cena == null || cena < num(f.cenaMin))) return false;
    if (f.cenaMax && (cena == null || cena > num(f.cenaMax))) return false;
    if (f.letnikMin && (letnik == null || letnik < num(f.letnikMin))) return false;
    if (f.letnikMax && (letnik == null || letnik > num(f.letnikMax))) return false;
    if (f.kmMin && (km == null || km < num(f.kmMin))) return false;
    if (f.kmMax && (km == null || km > num(f.kmMax))) return false;
    return true;
  }), [cars, f]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const active = Object.values(f).some((v) => v !== '');
  const tplDirty = tpl !== tplSaved || atPhone !== atPhoneSaved;
  const canSend = (tplSaved || '').trim().length > 0;

  // Ko user pošlje SMS, si takoj zapomnimo številko kot "poslano" (da ne pošilja dvakrat).
  const markSent = (phone) => setSentMap((m) => (m[normPhone(phone)] ? m : { ...m, [normPhone(phone)]: new Date().toISOString() }));

  return (
    <div>
      <Navigation />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: 16 }}>
        <h2 style={{ marginBottom: 4 }}>Gebrauchtwagen (AT)</h2>
        <div style={{ color: '#666', fontSize: 14, marginBottom: 12 }}>
          Privatni oglasi z gebrauchtwagen.at (Avstrija), zadnji dan. Telefon je avstrijski (+43).
          {' '}Prikazano: <b>{filtered.length}</b> / {cars.length}.
        </div>

        {/* Nemška SMS predloga (Faza 2a) */}
        <div style={{ border: '1px solid #d9e6ff', borderRadius: 10, padding: 12, marginBottom: 14, background: '#f5f9ff' }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>Nemška SMS predloga (Vorlage) 🇦🇹</div>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, color: '#555', fontWeight: 600, gap: 4, marginBottom: 8, maxWidth: 320 }}>
            Povratna številka za AT (+43) — na SMS se doda namesto slovenske
            <input value={atPhone} onChange={(e) => setAtPhone(e.target.value.slice(0, 18))} placeholder="+43 660 1234567" maxLength={18} style={{ padding: 7, boxSizing: 'border-box', width: '100%', border: '1px solid #ccc', borderRadius: 5, fontWeight: 400 }} />
            <span style={{ fontWeight: 400, color: '#888' }}>Prazno = uporabi slovensko številko iz SMS profila.</span>
          </label>
          <textarea
            value={tpl}
            onChange={(e) => setTpl(e.target.value.slice(0, MAX_MSG))}
            placeholder={DEFAULT_DE}
            rows={2}
            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6, padding: 8, fontSize: 14, resize: 'vertical' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, fontSize: 12, color: '#666' }}>
            <span>{tpl.length}/{MAX_MSG} znakov · pred sporočilo se doda tvoje ime + telefon iz SMS profila.</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {!tpl && <button type="button" onClick={() => setTpl(DEFAULT_DE)} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>Vstavi vzorec</button>}
              <button type="button" onClick={saveTpl} disabled={!tplDirty || savingTpl} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: tplDirty ? '#007AFF' : '#b8c4d0', color: '#fff', fontWeight: 600, cursor: tplDirty ? 'pointer' : 'default' }}>
                {savingTpl ? 'Shranjujem…' : tplDirty ? 'Shrani predlogo' : 'Shranjeno ✓'}
              </button>
            </div>
          </div>
        </div>

        {/* Filtri (brskanje + shranjen prikaz + samodejni SMS — poenoteno) */}
        <div style={{ border: '1px solid #e6e6e6', borderRadius: 10, padding: 12, marginBottom: 16, background: '#fafafa' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <label style={lbl}>Znamka
            <select value={f.znamka} onChange={set('znamka')} style={inp}>
              <option value="">Vse</option>
              {znamke.map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </label>
          <label style={lbl}>Model
            <input value={f.model} onChange={set('model')} placeholder="Išči po modelu..." style={inp} />
          </label>
          <label style={lbl}>Gorivo
            <select value={f.gorivo} onChange={set('gorivo')} style={inp}>
              <option value="">Vsi</option>
              {goriva.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </label>
          <label style={lbl}>Karoserija
            <select value={f.karoserija} onChange={set('karoserija')} style={inp}>
              <option value="">Vse</option>
              {karoserije.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
          <label style={lbl}>Cena (€) od–do
            <div style={row}><input value={f.cenaMin} onChange={set('cenaMin')} placeholder="Od" style={inpS} inputMode="numeric" /><input value={f.cenaMax} onChange={set('cenaMax')} placeholder="Do" style={inpS} inputMode="numeric" /></div>
          </label>
          <label style={lbl}>Letnik od–do
            <div style={row}><input value={f.letnikMin} onChange={set('letnikMin')} placeholder="Od" style={inpS} inputMode="numeric" /><input value={f.letnikMax} onChange={set('letnikMax')} placeholder="Do" style={inpS} inputMode="numeric" /></div>
          </label>
          <label style={lbl}>Prevoženi km od–do
            <div style={row}><input value={f.kmMin} onChange={set('kmMin')} placeholder="Od" style={inpS} inputMode="numeric" /><input value={f.kmMax} onChange={set('kmMax')} placeholder="Do" style={inpS} inputMode="numeric" /></div>
          </label>
          <label style={lbl}>Lokacija (AT)
            <input value={f.lokacija} onChange={set('lokacija')} placeholder="mesto / PLZ..." style={inp} />
          </label>
          </div>

          {/* Noga: počisti + samodejni SMS + shrani (poenoten filter). */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginTop: 12, paddingTop: 12, borderTop: '1px solid #ececec' }}>
            <button onClick={() => setF(EMPTY)} disabled={!active} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #ccc', background: active ? '#fff' : '#f0f0f0', cursor: active ? 'pointer' : 'default' }}>Počisti filtre</button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <input type="checkbox" checked={autoEnabled} onChange={(e) => setAutoEnabled(e.target.checked)} />
              🤖 Samodejno pošiljaj nemški SMS za nove ujemajoče oglase
            </label>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
              {autoEnabled && !(tplSaved || '').trim() && <span style={{ fontSize: 12, color: '#c0392b', fontWeight: 600 }}>Najprej shrani nemško predlogo zgoraj!</span>}
              <button type="button" onClick={saveFilter} disabled={!filterDirty || savingFilter} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: filterDirty ? '#e67e22' : '#d9b48a', color: '#fff', fontWeight: 600, cursor: filterDirty ? 'pointer' : 'default' }}>
                {savingFilter ? 'Shranjujem…' : filterDirty ? 'Shrani filter' : 'Shranjeno ✓'}
              </button>
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
            Shranjen filter velja kot tvoj privzeti pogled ob vsakem odprtju (na vseh napravah) in — če je vklopljeno samodejno pošiljanje — kot pogoj za nemški SMS. Prazna polja = brez omejitve.
          </div>
        </div>

        {loading ? <Spinner /> : filtered.length === 0 ? (
          <div style={{ color: '#888', padding: 24 }}>{cars.length === 0 ? 'Ni oglasov (scraper jih postopoma nabira).' : 'Noben oglas ne ustreza filtrom.'}</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
            {filtered.map((c) => (
              <GwCard key={c.id} c={c} canSend={canSend} template={tplSaved} sentAt={sentMap[normPhone(c.phone)]} onSent={markSent} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const lbl = { display: 'flex', flexDirection: 'column', fontSize: 12, color: '#555', gap: 4, fontWeight: 600 };
const inp = { padding: 7, boxSizing: 'border-box', width: '100%', border: '1px solid #ccc', borderRadius: 5, fontWeight: 400 };
const inpS = { padding: 7, boxSizing: 'border-box', width: '100%', border: '1px solid #ccc', borderRadius: 5, fontWeight: 400 };
const row = { display: 'flex', gap: 6 };

function GwCard({ c, canSend, template, sentAt, onSent }) {
  const title = `${c.znamka || ''} ${c.model || ''}`.trim() || 'Neznano vozilo';
  const km = c.mileage && !isNaN(Number(c.mileage)) ? `${Number(c.mileage).toLocaleString('de-AT')} km` : null;
  const meta = [c.letnik, c.gorivo, km, c.karoserija].filter(Boolean).join(' · ');
  // Povezava do oglasa — samo UUID zadošča (gebrauchtwagen.at 308-preusmeri na pravi slug).
  const adUrl = c.externalId ? `https://www.gebrauchtwagen.at/fahrzeuge/${c.externalId}` : null;
  const img = c.imageUrl ? (
    <img src={c.imageUrl} alt={title} style={{ width: '100%', height: 170, objectFit: 'cover', background: '#f2f2f2', display: 'block' }} loading="lazy" />
  ) : null;

  const [state, setState] = useState('idle'); // idle | sending | sent | error
  const [note, setNote] = useState('');
  const already = !!sentAt || state === 'sent';

  const sendSms = async () => {
    if (!c.phone || !canSend || state === 'sending') return;
    setState('sending'); setNote('');
    try {
      const res = await apiService.sendSms({
        externalId: c.externalId,
        recipientPhone: c.phone,
        message: template,
        filterUsed: 'Gebrauchtwagen (AT) — ročno',
      });
      const ok = res && res.data && (res.data.sent || res.data.status === 'OK');
      setState('sent');
      setNote(ok ? 'Poslano ✓' : (res && res.data && res.data.note) || 'Zabeleženo');
      onSent(c.phone);
    } catch (e) {
      const msg = e && e.response && e.response.data && e.response.data.error;
      // 409 = blok-lista ali že poslano; to je pričakovano stanje, ne rdeča napaka.
      const soft = e && e.response && e.response.status === 409;
      setState(soft ? 'sent' : 'error');
      setNote(msg || 'Napaka pri pošiljanju');
      if (soft) onSent(c.phone);
    }
  };

  return (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
      {adUrl && img ? <a href={adUrl} target="_blank" rel="noopener noreferrer">{img}</a> : img}
      <div style={{ padding: 12 }}>
        <div style={{ fontWeight: 700 }}>
          {adUrl ? <a href={adUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{title} ↗</a> : title}
        </div>
        <div style={{ color: '#555', fontSize: 13, marginTop: 2 }}>{meta}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: '#007AFF' }}>{formatPrice(c.cena)}</span>
          <span style={{ fontSize: 13, color: '#333' }}>{c.viewLocation || ''}</span>
        </div>
        {c.phone ? (
          <a href={`tel:${c.phone}`} style={{ display: 'inline-block', marginTop: 8, fontWeight: 600, color: '#080', textDecoration: 'none' }}>📞 {c.phone}</a>
        ) : (
          <div style={{ marginTop: 8, color: '#999', fontSize: 13 }}>ni telefona</div>
        )}

        {/* Ročni nemški SMS */}
        {c.phone && (
          <div style={{ marginTop: 10, borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
            {sentAt && state === 'idle' && (
              <div style={{ fontSize: 12, color: '#a06a00', marginBottom: 6 }}>⚠️ Tej številki si že poslal ({String(sentAt).slice(0, 10)}).</div>
            )}
            <button
              type="button"
              onClick={sendSms}
              disabled={!canSend || state === 'sending' || already}
              title={!canSend ? 'Najprej shrani nemško predlogo zgoraj.' : ''}
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 6, border: 'none', fontWeight: 600,
                cursor: (!canSend || state === 'sending' || already) ? 'default' : 'pointer',
                background: already ? '#e8f5e9' : (!canSend ? '#eee' : '#25a244'),
                color: already ? '#2e7d32' : (!canSend ? '#999' : '#fff'),
              }}
            >
              {state === 'sending' ? 'Pošiljam…' : already ? 'SMS poslan ✓' : '✉️ Pošlji SMS (DE)'}
            </button>
            {note && state !== 'idle' && (
              <div style={{ fontSize: 12, marginTop: 6, color: state === 'error' ? '#c0392b' : '#2e7d32' }}>{note}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
